import { SnsApiS, SnsErrorS, type SnsTypeS } from '@/features/Sns/outbound/share';
import { MCO_DOMAIN, MCO_MARKETING_PATH, MCO_PRIVACY_PATH, MCO_TERMS_PATH } from '@/libs/constant';
import type { ErrorJson } from '@/libs/errors';
import { AT, T } from '@/libs/translation';
import { makeResultUrl } from '@/libs/util/makeServiceUrl';
import { Button, ButtonSnsIcon, Checkbox, Icon, Link, Typo } from '@/ui/component/atom';
import { Header, Layout } from '@/ui/component/cell';
import { LogoView } from '@/ui/component/view';
import { debounce, getSharedData } from '@/ui/util';
import { each, filter, identity, map, pipe, reject, toArray } from '@fxts/core';
import { isAxiosError } from 'axios';
import { html, type Html, Page, View } from 'rune-ts';
import { PostMessage } from '../../../util/postMessage';
import { makeErrorPopup } from '../../view/Popup/makeErrorPopup';
import auth_klass from '../Common/Auth.module.scss';
import klass from './SignupSnsPage.module.scss';

export interface SignupSnsPageData {
  sns_type: SnsTypeS.SnsType;
  email: string;
  nickname: string;
  sns_request_referer?: string;
  nickname_validation: SnsTypeS.SnsUserDataType['nickname_validation'];
}

export class SignupSnsPageBodyView extends View<SignupSnsPageData> {
  component: {
    submit: Button;
    all_agree: Checkbox;
    marketing: Checkbox;
  };

  constructor(data: SignupSnsPageData) {
    super(data);

    this.component = {
      submit: new Button(
        {
          label: new Typo({ text: T('auth.signup.complete') }, { weight: 'bold' }),
        },
        {
          disabled: true,
          size: 'large',
        },
      ),
      marketing: new Checkbox({
        label: new Typo({ text: T('auth.signup.agree_marketing_terms') }),
        name: 'is_agree_notification',
      }),
      all_agree: new Checkbox({
        label: new Typo({ text: T('auth.signup.agree_all') }, { typo: 'p1', weight: 'bold' }),
        name: 'all_agree',
      }),
    };
  }

  override template() {
    const { theme, device } = getSharedData(this);
    const { sns_type, email, nickname, nickname_validation } = this.data;

    return html` <div class="${auth_klass.page}">
      <div class="${auth_klass.container}">
        ${new LogoView({ theme })}
        <div class="${auth_klass.section}">
          <form class="${klass.signup} signup">
            <div class="${klass.info_container}">
              ${new ButtonSnsIcon({ type: sns_type }, { active: true })}
              <div class="${klass.info_wrapper}">
                <div class="${klass.info_text_wrapper}">
                  ${new Typo({ text: T('auth.signup.sns.continue_guide', { sns_type: T(`common.${sns_type}`) }) })}
                  ${new Typo({ text: T('auth.signup.sns.signup_guide') }, { typo: 'p2', color: 'gray_80' })}
                </div>
                <div class="${klass.info_data_wrapper}">
                  <div class="${klass.info_data_row}">
                    ${new Typo({ text: T('common.email') }, { typo: 'p2', color: 'gray_80', klass: klass.title })}
                    ${new Typo({ text: email }, { typo: 'p2', klass: klass.email })}
                  </div>
                  <div class="${klass.info_data_row}">
                    ${new Typo({ text: T('common.nickname') }, { typo: 'p2', color: 'gray_80', klass: klass.title })}
                    <div class="${klass.nickname_wrapper}">
                      ${new Typo({ text: nickname }, { typo: 'p2' })}
                      ${!nickname_validation.exist || !nickname_validation.rule
                        ? html` <div class="${klass.nickname_wrapper}" data-row="true">
                            ${new Icon({ name: 'info_color', size: 20, klass: klass.info_icon })}
                            ${new Typo(
                              {
                                text: html.preventEscape(
                                  T('auth.signup.random_nickname_guide', {
                                    context: !nickname_validation.rule ? 'breach' : undefined,
                                    br: device === 'mo' ? '<br/>' : ' ',
                                  }),
                                ),
                              },
                              { typo: 'p2' },
                            )}
                          </div>`
                        : ''}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="${klass.agreement_container}">
              <div class="${klass.all}">${this.component.all_agree}</div>
              <div class="${klass.item_container}">
                ${AT.language === 'kr'
                  ? html`
                      <div class="${klass.item}">
                        ${new Checkbox({
                          label: new Typo({ text: T('auth.signup.agree_age_terms') }),
                          name: 'age_agree',
                        })}
                      </div>
                    `
                  : ''}
                <div class="${klass.item}">
                  ${new Checkbox({
                    label: new Typo({ text: T('auth.signup.agree_usage_terms') }),
                    name: 'usage_agree',
                  })}
                  ${new Link(
                    {
                      label: new Typo({ text: T('common.see') }, { underline: true, color: 'gray_50' }),
                      href: MCO_TERMS_PATH,
                    },
                    {
                      target: '_blank',
                      klass: `see_more ${klass.see_more}`,
                    },
                  )}
                </div>
                <div class="${klass.item}">
                  ${new Checkbox({
                    label: new Typo({ text: T('auth.signup.agree_privacy_terms') }),
                    name: 'privacy_agree',
                  })}
                  ${new Link(
                    {
                      label: new Typo({ text: T('common.see') }, { underline: true, color: 'gray_50' }),
                      href: MCO_PRIVACY_PATH,
                    },
                    {
                      target: '_blank',
                      klass: `see_more ${klass.see_more}`,
                    },
                  )}
                </div>
                <div class="${klass.item}">
                  ${this.component.marketing}
                  ${new Link(
                    {
                      label: new Typo({ text: T('common.see') }, { underline: true, color: 'gray_50' }),
                      href: MCO_MARKETING_PATH,
                    },
                    {
                      target: '_blank',
                      klass: `see_more ${klass.see_more}`,
                    },
                  )}
                </div>
              </div>
            </div>
            <div class="${klass.button_container}">
              ${new Button(
                {
                  label: new Typo({ text: T('common.back') }, { weight: 'bold' }),
                },
                {
                  size: 'large',
                  type: 'button',
                  klass: `${klass.button} back`,
                  color: 'line',
                },
              )}
              ${this.component.submit}
            </div>
          </form>
        </div>
      </div>
    </div>`;
  }

  override onRender() {
    this.delegate('click', '.back', () => {
      if (this.data.sns_request_referer) {
        location.replace(this.data.sns_request_referer);
      } else {
        history.back();
      }
    });

    const formChangeHandler = debounce(this.formChangeHandler.bind(this), 300);
    this.delegate('keyup', '.signup', formChangeHandler);
    this.delegate('change', '.signup', formChangeHandler);
    this.delegate('submit', '.signup', this.formSubmitHandler);
    this.delegate('change', Checkbox, this.chechBoxChangeHandler);
    this.delegate('click', '.see_more', this.linkClickHandler);
  }

  isCheckAgree(reject_keys: string[]): boolean {
    const rest_checkboxes = pipe(
      this.subViews(Checkbox),
      reject((checkbox) => reject_keys.includes(checkbox.data.name)),
      toArray,
    );

    return pipe(
      rest_checkboxes,
      map((checkbox) => checkbox.isChecked()),
      filter(identity),
      toArray,
      (check_list) => check_list.length === rest_checkboxes.length,
    );
  }

  linkClickHandler(e: Event & { originalEvent: Event }) {
    const { app_info } = getSharedData(this);

    if (app_info?.name === 'MarppleShop') {
      e.originalEvent.preventDefault();
      PostMessage.goEmptyScreen({ uri: (e.currentTarget as HTMLAnchorElement).href });
    }
  }

  chechBoxChangeHandler(_e: Event, view: Checkbox) {
    if (view.data.name === this.component.all_agree.data.name) {
      each((checkbox) => (view.isChecked() ? checkbox.activate() : checkbox.deactivate()), this.subViews(Checkbox));
    } else {
      this.isCheckAgree(['all_agree']) ? this.component.all_agree.activate() : this.component.all_agree.deactivate();
    }
  }

  async formChangeHandler(_: Event) {
    const agree_check = this.isCheckAgree(['all_agree', 'is_agree_notification']);

    if (agree_check && this.component.submit.option.disabled) {
      this.component.submit.option.disabled = false;
      this.component.submit.redraw();
      return;
    }

    if (!agree_check && !this.component.submit.option.disabled) {
      this.component.submit.option.disabled = true;
      this.component.submit.redraw();
    }
  }
  async formSubmitHandler(e: Event & { originalEvent: Event }) {
    e.originalEvent.preventDefault();
    const { client_id } = getSharedData(this);
    try {
      await SnsApiS.postSnsSignup({ is_agree_notification: this.component.marketing.isChecked() });

      return location.replace(makeResultUrl(client_id, { signup: true }));
    } catch (e) {
      if (isAxiosError<ErrorJson>(e)) {
        if (e.response?.data.code === 'SNS_006') {
          const error = SnsErrorS[e.response.data.code]();
          const res = await makeErrorPopup(error, {
            ...error.json,
            ok: T('auth.signup.go_login'),
            cancel: T('common.ok'),
          });

          if (res === 'confirm') {
            const { client_id } = getSharedData(this);
            location.href = `/${AT.language}/login?client_id=${client_id}`;
          }

          return;
        }

        return makeErrorPopup(e, e.response?.data);
      }

      return makeErrorPopup(e);
    }
  }
}

export class SignupSnsPage extends Page<SignupSnsPageData> {
  protected override template(): Html {
    return html`
      <div>
        ${new Layout(
          { view: new SignupSnsPageBodyView(this.data) },
          {
            fullwidth: true,
            header: this.sharedData?.device === 'mo' ? new Header({}) : undefined,
          },
        )}
      </div>
    `;
  }

  override onRender() {
    this.delegate(Header.HeaderBackEvent, Header, () => {
      if (this.data.sns_request_referer) {
        location.replace(this.data.sns_request_referer);
      } else {
        history.back();
      }
    });
  }
}
